import React, { useEffect, useRef, useCallback, useState } from 'react';
import ZoomUIToolkit from '@zoom/videosdk-ui-toolkit';
import '@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
import useZoomSession from '../../hooks/useZoomSession';
import useResponsive, { injectHeaderGapFix } from '../../hooks/useResponsive';
import useUnifiedAudioRecorder from '../../hooks/useUnifiedAudioRecorder';
import AudioVisualizer from '../shared/AudioVisualizer';
import { fetchZoomToken } from '../../services/api';
import { fetchBackendApi, sendMessageToParent } from '../../utils/apiUtils';
import { saveZoomSession } from '../../utils/sessionStorage';
// import { startRTMPStream, stopRTMPStream } from '../../services/RTMPStreamingService';

/**
 * ZoomVideoSDK - Pure Zoom SDK integration component
 * Handles only video conferencing functionality
 */
function ZoomVideoSDK({ 
  sessionToJoin, zoomTopic, zoomPasscode, miraSolCaseId, providerInfo, 
  healthcareInfo, onTranscriptUpdate, onSpeakerSegmentsUpdate, onSoapNoteUpdate
}) {
  const { viewport, getZoomUIConfig } = useResponsive();
  const {
    startRecording,
    stopRecording,
    endSession,
    isRecording: isAudioRecording,
    connectionState: connectionStatus,
    transcription,
    visitNote,
    error: audioError,
    audioLevel,
    microphoneStatus
  } = useUnifiedAudioRecorder();
  const uiToolkitContainerRef = useRef(null);
  const uiToolkitRef = useRef(null);
  
  // Local state for UI
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentContainerId, setCurrentContainerId] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isAutoJoining, setIsAutoJoining] = useState(false);
  const [autoJoinMeetingName, setAutoJoinMeetingName] = useState('');
  const [activeZoomSessionId, setActiveZoomSessionId] = useState(null);
  const maxRetries = 3;
  const retryDelayMs = 3000;

  // injectHeaderGapFix is now imported from useResponsive

  // Emergency Leave trigger (workaround for mobile overlay hitboxes)
  const triggerLeave = useCallback(() => {
    try {
      const container = uiToolkitContainerRef.current;
      const doc = container?.ownerDocument || document;
      const cid = container?.id;
      if (!cid) return;

      const selectors = [
        `#${cid} .uikit-mobile-header [aria-label*="Leave"]`,
        `#${cid} .uikit-mobile-header [aria-label*="End"]`,
        `#${cid} .uikit-mobile-header [data-testid*="leave"]`,
        `#${cid} [data-testid*="leave"]`,
        `#${cid} [data-test*="leave"]`,
        `#${cid} button[title*="Leave"]`,
        `#${cid} button[title*="leave"]`,
        `#${cid} [class*="leave"]`,
      ].join(',');

      const btn = doc.querySelector(selectors);
      if (btn && typeof btn.click === 'function') {
        btn.click();
      } else {
        console.warn('[ZoomVideoSDK] Leave button not found in header');
        // Fallback: try toolkit API if available
        try {
          if (typeof uiToolkitRef.current?.leaveSession === 'function') {
            uiToolkitRef.current.leaveSession();
          } else if (typeof uiToolkitRef.current?.endSession === 'function') {
            uiToolkitRef.current.endSession();
          }
        } catch (e) {
          console.warn('[ZoomVideoSDK] Fallback leave failed', e);
        }
      }
    } catch (e) {
      console.warn('[ZoomVideoSDK] triggerLeave error', e);
    }
  }, []);

  // RTMP destination for AWS IVS (can be overridden via env)
  const IVS_RTMP_URL = process.env.REACT_APP_IVS_RTMP_URL || 'rtmps://c05d1f146c23.global-contribute.live-video.net:443/app/sk_us-east-1_9jQco8AP3Ck0_cBHc1BCuQgk0DaCIymzaEtQwEcQUE7';

  const {
    session, zoomError, isHost, isRecording, inviteLink, networkStatus, joinMeeting,
    handleSessionClosed, loadStoredSession, setZoomError, setIsRecording, setNetworkStatus,
    getCorrectMapping
  } = useZoomSession({ miraSolCaseId });

  // Function to join the Zoom session using UI Toolkit
  const joinSessionAsync = useCallback(async (sessionId, topic, passcode, userName, role, isRetry = false) => {
    if (!uiToolkitContainerRef.current || !uiToolkitRef.current) {
      console.error('[ZoomVideoSDK] UI Toolkit container or ref not available');
      return;
    }

    // Prevent duplicate calls
    if (isConnecting || isSessionActive) {
      console.log('[ZoomVideoSDK] Already joining/active, ignoring duplicate call');
      return;
    }

    // Check network connectivity before attempting to join
    if (!navigator.onLine) {
      console.error('[ZoomVideoSDK] No internet connection detected');
      setConnectionError({
        type: 'NETWORK_OFFLINE',
        message: 'No internet connection',
        canRetry: true
      });
      return;
    }

    try {
      // Set connecting state
      setIsConnecting(true);
      setIsSessionActive(false);
      setConnectionError(null); // Clear any previous errors
      console.log('[ZoomVideoSDK] Preparing to join session:', { sessionId, topic, userName, role, isRetry, retryCount });

      // Create unique container ID for this session
      const uniqueContainerId = `video-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('[ZoomVideoSDK] Generated unique container ID:', uniqueContainerId);
      setCurrentContainerId(uniqueContainerId);

      // Use provided parameters or fallback to props
      const currentSessionId = sessionId || sessionToJoin || session?.sessionId;
      const currentUserName = userName || providerInfo?.name || healthcareInfo?.patientName || 'Participant';
      
      // Get correct mapping for topic and passcode
      const topicValue = topic || zoomTopic || session?.topic;
      const passcodeValue = passcode || zoomPasscode || session?.passcode || '';
      const { topic: currentTopic, passcode: currentPasscode } = getCorrectMapping(topicValue, passcodeValue);
      
      const currentRole = role || 'attendee';

      // Fetch token from backend
      const tokenData = await fetchZoomToken({
        session_id: currentSessionId,
        role: currentRole,
        topic: currentTopic,
        passcode: currentPasscode
      });

      const videoSDKJWT = tokenData.token;
      if (!videoSDKJWT) {
        throw new Error('Missing session token from backend');
      }

      console.log('[ZoomVideoSDK] Token received, joining with topic:', currentTopic);

      // Prepare container with unique ID
      const container = uiToolkitContainerRef.current;
      container.id = uniqueContainerId;
      // Apply minimal gap fix for mobile devices to ensure header/content starts at top
      if (viewport.isMobile) {
        const doc = container?.ownerDocument || document;
        injectHeaderGapFix(doc, uniqueContainerId);
      }
      
      // Apply responsive container styling
      const currentViewport = viewport;
      const containerStyle = currentViewport.isMobile ? {
        width: '100vw', height: '100vh', position: 'fixed', top: '0', left: '0', zIndex: '1000'
      } : currentViewport.isTablet ? {
        width: '100%', height: '70vh', position: 'relative'
      } : {
        width: '100%', height: '100%', position: 'relative'
      };

      Object.assign(container.style, containerStyle);

      // Create Zoom UI configuration
      const baseConfig = {
        sessionName: currentTopic,
        videoSDKJWT: videoSDKJWT,
        userName: currentUserName,
        sessionIdleTimeoutMins: 3600,
        sessionPasscode: currentPasscode,
        debug: false,
        // Direct pagination configuration
        pagination: false,
        // Force all participants to be visible without pagination
        layoutConfig: {
          mode: 'gallery',
          maxVideosForGallery: 25,
          disablePagination: true
        }
      };

      // Use browser detection for additional Firefox/Safari fixes
      const userAgent = navigator.userAgent.toLowerCase();
      const isFirefox = userAgent.indexOf('firefox') > -1;
      const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1;
      
      // Configure for all browsers to ensure consistent behavior
      baseConfig.forceRender = true;
      baseConfig.videoConstraints = {
        width: { min: 320, ideal: 640, max: 1280 },
        height: { min: 180, ideal: 360, max: 720 },
        frameRate: { max: 30 },
        facingMode: 'user'
      };
      
      // Force grid view settings for all browsers
      baseConfig.layoutConfig = {
        mode: 'gallery',
        maxVideosForGallery: 25,
        disablePagination: true,
        alwaysShowGalleryView: true,
        showOnlyVideoParticipants: true
        // IMPORTANT: Do NOT add a function here (e.g., `filter`) as Zoom UI Toolkit stores
        // this config inside a Redux store. Functions are non-serializable and will trigger
        // errors like: "A non-serializable value was detected in the state".
        // If you ever need participant filtering, implement it via Zoom's built-in options
        // or via a separate observable, not in layoutConfig.
      };
      
      // Override view mode settings
      baseConfig.viewModes = ['gallery'];
      baseConfig.defaultViewMode = 'gallery';

      const config = getZoomUIConfig(baseConfig);

      // Add invite link to config if available
      if (inviteLink) {
        config.featuresOptions.invite.inviteLink = inviteLink;
      }

      console.log('[ZoomVideoSDK] Joining session with config. Container ID:', container.id);
      const joinResult = await uiToolkitRef.current.joinSession(container, config);

      // Set up event listeners - pass the current session info for proper saving
      setupEventListeners(tokenData, {
        sessionId: currentSessionId,
        topic: currentTopic,
        passcode: currentPasscode,
        userName: currentUserName,
        role: currentRole
      });

      // Handle promise-like joinResult
      if (joinResult && typeof joinResult.catch === 'function') {
        joinResult.catch(error => {
          console.error('[ZoomVideoSDK] Join session promise error:', error);
          handleJoinError(error);
        });
      }

    } catch (error) {
      setIsConnecting(false);
      console.error('[ZoomVideoSDK] Error in joinSessionAsync:', error);
      setZoomError({
        title: "Join Session Error",
        message: error.message || "An unexpected error occurred while trying to join the session.",
        suggestion: "Please try again. If the problem persists, contact support."
      });
    }
  }, [sessionToJoin, zoomTopic, zoomPasscode, providerInfo, healthcareInfo, getZoomUIConfig, inviteLink, getCorrectMapping, setZoomError]);

  // Set up Zoom event listeners
  const setupEventListeners = useCallback((tokenData, currentSessionInfo = null) => {
    const toolkit = uiToolkitRef.current;
    if (!toolkit) {
      console.error('[ZoomVideoSDK] No toolkit available for event listeners');
      return;
    }

    console.log('[ZoomVideoSDK] Setting up event listeners');

    // Session joined listener
    if (typeof toolkit.onSessionJoined === 'function') {
      toolkit.onSessionJoined(() => {
        console.log('[ZoomVideoSDK] Session joined successfully');
        setIsConnecting(false);
        setIsSessionActive(true);
        setConnectionError(null); // Clear any connection errors
        setRetryCount(0); // Reset retry count on successful connection
        setIsAutoJoining(false); // Reset auto-join flag
        setAutoJoinMeetingName(''); // Clear auto-join meeting name
        
        // FIREFOX/SAFARI FIX: Configure video properly instead of using turnOnVideo
        try {
          setTimeout(() => {
            // Fix for Firefox/Safari video after a brief delay
            const userAgent = navigator.userAgent.toLowerCase();
            const isFirefox = userAgent.indexOf('firefox') > -1;
            const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1;
            
            if (isFirefox || isSafari) {
              console.log('[ZoomVideoSDK] Applying Firefox/Safari video view fix');
              // Don't call turnOnVideo directly as it may cause duplicate videos
              // Instead, configure video layout if available
              if (toolkit.configVideo && typeof toolkit.configVideo === 'function') {
                toolkit.configVideo({ 
                  viewMode: 'gallery',  // Use gallery view to show all participants
                  forceRender: true,    // Force video rendering
                  // Force all videos on one screen without pagination
                  maxVideosPerPage: 25, // High number to avoid pagination
                  layoutType: 'grid'    // Grid layout to show all videos on one screen
                });
                
                // Ensure video container can fit multiple participants
                const container = uiToolkitContainerRef.current;
                if (container) {
                  // Add CSS to optimize the video container for multiple videos
                  container.style.display = 'flex';
                  container.style.flexWrap = 'nowrap';
                  container.style.overflow = 'hidden';
                }
              }
            }
          }, 1000);
        } catch (e) {
          console.warn('[ZoomVideoSDK] Video fix error:', e);
        }
        
        // Save session to localStorage for auto-rejoin on refresh
        // Use currentSessionInfo which has the actual session ID we're joining
        const sessionData = {
          sessionId: currentSessionInfo?.sessionId || sessionToJoin || tokenData?.session_id,
          passcode: currentSessionInfo?.passcode || zoomPasscode || tokenData?.passcode || '',
          topic: currentSessionInfo?.topic || zoomTopic || tokenData?.topic,
          attendeeName: currentSessionInfo?.userName || providerInfo?.name || healthcareInfo?.patientName || 'Provider',
          role: 'host'
        };
        saveZoomSession(sessionData);
        
        // Start unified audio recording for Zoom (only if we have a case ID)
        if (miraSolCaseId) {
          // startRecording('zoom', miraSolCaseId, {
          //   sessionId: tokenData?.session_id,
          //   providerInfo,
          //   healthcareInfo
          // }).catch(error => {
          //   console.error('[Zoom] Failed to start audio recording:', error);
            // Continue with video session even if audio recording fails
          // });
        } else {
          console.warn('[Zoom] No miraSolCaseId available, skipping audio recording');
        }
        sendMessageToParent('sessionStarted', {
          miraSolCaseId,
          sessionId: tokenData?.session_id
        });

        // Start direct RTMP streaming
        const zSessionId = currentSessionInfo?.sessionId || tokenData?.session_id || 'default-stream';
        setActiveZoomSessionId(zSessionId);
        
        if (IVS_RTMP_URL) {
          console.log('[ZoomVideoSDK][RTMP] Starting RTMP stream', { 
            streamKey: zSessionId,
            rtmpUrl: IVS_RTMP_URL
          });
          
          // Start screen sharing to RTMP
          // startRTMPStream(IVS_RTMP_URL, {
          //   videoBitrate: 2500,
          //   audioBitrate: 128
          // })
          // .then(stream => {
          //   console.log('[ZoomVideoSDK][RTMP] Screen sharing started for RTMP');
          //   // You can add the stream to a video element if needed
          //   // const video = document.createElement('video');
          //   // video.srcObject = stream;
          //   // video.play();
          // })
          // .catch(err => {
          //   console.error('[ZoomVideoSDK][RTMP] Failed to start RTMP stream', err);
          // });
        } else {
          console.warn('[ZoomVideoSDK][RTMP] No RTMP URL provided');
        }
      });
    } else {
      console.warn('[Zoom] onSessionJoined not available');
    }

    // Session closed listener - handles all session termination scenarios
    if (typeof toolkit.onSessionClosed === 'function') {
      toolkit.onSessionClosed(() => {
        setIsSessionActive(false);
        setIsConnecting(false);
        // Stop unified audio recording and end session
        stopRecording();
        endSession();
        handleSessionClosed();

        // Stop RTMP streaming
        console.log('[ZoomVideoSDK][RTMP] Stopping RTMP stream');
        // stopRTMPStream();
        setActiveZoomSessionId(null);
      });
    } else {
      console.warn('[Zoom] onSessionClosed not available');
    }

    // Session destroyed listener - clean up toolkit for next session
    if (typeof toolkit.onSessionDestroyed === 'function') {
      toolkit.onSessionDestroyed(() => {
        console.log('[Zoom] Session destroyed - cleaning up toolkit');
        try {
          // Safety: also try to stop RTMP stream if still running
          if (activeZoomSessionId) {
            console.log('[ZoomVideoSDK][RTMP] onSessionDestroyed -> stopping RTMP stream', { activeZoomSessionId });
            // stopRTMPStream();
            setActiveZoomSessionId(null);
          }
          toolkit.destroy();
          console.log('[Zoom] Toolkit destroyed successfully');
          // Reinitialize for next session
          uiToolkitRef.current = ZoomUIToolkit;
        } catch (error) {
          console.warn('[Zoom] Error during toolkit destruction:', error);
        }
      });
    } else {
      console.warn('[Zoom] onSessionDestroyed not available');
    }

    // Participant listeners
    if (typeof toolkit.onParticipantJoined === 'function') {
      toolkit.onParticipantJoined((participant) => {
        // console.log('[Zoom] Participant joined:', participant);
        sendMessageToParent('participantJoined', { miraSolCaseId, participant });
      });
    }

    if (typeof toolkit.onParticipantLeft === 'function') {
      toolkit.onParticipantLeft((participant) => {
        // console.log('[Zoom] Participant left:', participant);
        sendMessageToParent('participantLeft', { miraSolCaseId, participant });
      });
    }

    // Error listener
    if (typeof toolkit.onError === 'function') {
      toolkit.onError((error) => {
        sendMessageToParent('zoomError', { miraSolCaseId, error });
      });
    }
  }, [miraSolCaseId, 
    startRecording, stopRecording, endSession, handleSessionClosed, sessionToJoin, zoomTopic, zoomPasscode, providerInfo, healthcareInfo]);

  // Handle join errors with retry logic
  const handleJoinError = useCallback((error) => {
    setIsConnecting(false); // Reset connecting state on any error
    
    // Check if error is a connection error that can be retried
    const isConnectionError = error?.type === 'JOIN_MEETING_FAILED' && 
                            (error?.errorCode === 200 || error?.reason?.includes('connection'));
    
    if (isConnectionError && retryCount < maxRetries) {
      console.log(`[Zoom] Connection error, retrying in ${retryDelayMs}ms (attempt ${retryCount + 1}/${maxRetries})`);
      setConnectionError({
        type: 'CONNECTION_ERROR',
        message: `Connection failed. Retrying... (${retryCount + 1}/${maxRetries})`,
        canRetry: true,
        isRetrying: true
      });
      
      // Store session data for retry
      const retryData = {
        sessionId: sessionToJoin,
        topic: zoomTopic,
        passcode: zoomPasscode,
        userName: providerInfo?.name || 'Provider',
        role: 'host'
      };
      
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        joinSessionAsync(
          retryData.sessionId,
          retryData.topic,
          retryData.passcode,
          retryData.userName,
          retryData.role,
          true
        );
      }, retryDelayMs);
    } else {
      // Max retries reached or non-retriable error
      setConnectionError({
        type: error?.type || 'UNKNOWN_ERROR',
        message: error?.reason || error?.message || 'Failed to join meeting',
        canRetry: true,
        isRetrying: false
      });
      
      if (error && error.type === 'JOIN_MEETING_FAILED') {
        setZoomError({
          title: "Failed to Join Meeting",
          message: error.reason || "Could not join the meeting. Please check account or network.",
          suggestion: "Verify Zoom account credentials and internet. Contact support if issues persist."
        });
      } else if (error) {
        setZoomError({
          title: "Session Start Error",
          message: error.message || error.reason || "Failed to start session.",
          suggestion: "Please try again or check network/account."
        });
      }
    }
  }, [setZoomError, retryCount, sessionToJoin, zoomTopic, zoomPasscode, providerInfo, joinSessionAsync]);

  // Initialize Zoom UI Toolkit and handle auto-join
  useEffect(() => {
    uiToolkitRef.current = ZoomUIToolkit;
    // console.log('[Zoom] Zoom UI Toolkit initialized - useEffect triggered with:', {
    //   zoomTopic, zoomPasscode, miraSolCaseId, sessionToJoin, 
    //   providerInfo: providerInfo?.name, healthcareInfo: healthcareInfo?.name
    // });

    // Auto-join from props if available
    if (zoomTopic && zoomPasscode && miraSolCaseId && providerInfo && healthcareInfo) {
      const userName = providerInfo?.name || healthcareInfo?.patientName || 'Participant';
      joinSessionAsync(sessionToJoin, zoomTopic, zoomPasscode, userName, 'host');
    } else {
      // Fallback to stored session
      const storedSession = loadStoredSession();
      console.log('[Zoom] Checking for stored session:', storedSession);
      if (storedSession) {
        console.log('[Zoom] Found stored session, auto-joining:', storedSession);
        setIsAutoJoining(true);
        setAutoJoinMeetingName(storedSession.topic);
        // Add 3 second delay to show the awesome auto-join message
        setTimeout(() => {
          joinSessionAsync(
            storedSession.sessionId,
            storedSession.topic,
            storedSession.passcode,
            storedSession.attendeeName,
            storedSession.role
          );
        }, 3000);
      } else {
        console.log('[Zoom] No stored session found');
      }
    }

    // Global error handler
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      if (reason && reason.type === 'JOIN_MEETING_FAILED') {
        handleJoinError(reason);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Cleanup function - destroy toolkit when component unmounts
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      // Only attempt to destroy if we have a valid toolkit instance and it's been properly initialized
      if (uiToolkitRef.current && 
          typeof uiToolkitRef.current.destroy === 'function' && 
          uiToolkitRef.current !== ZoomUIToolkit) {  // Check if it's not just the initial toolkit class
        try {
          uiToolkitRef.current.destroy();
          console.log('[Zoom] Successfully cleaned up UI Toolkit');
        } catch (destroyError) {
          // Only log the error if it's not about missing config
          if (!destroyError.message.includes('UIToolkit config is required')) {
            console.error('[Zoom] Error during cleanup destroy:', destroyError);
          }
        } finally {
          // Ensure we have a clean slate for the next session
          uiToolkitRef.current = ZoomUIToolkit;
        }
      }
    };
  }, [
    zoomTopic, 
    zoomPasscode, 
    miraSolCaseId, 
    sessionToJoin
    // Removed callback dependencies that cause re-renders
  ]);

  // Removed duplicate session state trigger - props-based auto-join handles video visits

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Zoom] Network is online');
      setNetworkStatus('good');
    };

    const handleOffline = () => {
      console.log('[Zoom] Network is offline');
      setNetworkStatus('poor');
    };

    // Initial check
    if (navigator.onLine) {
      handleOnline();
    } else {
      handleOffline();
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setNetworkStatus]);

  // Handle updates from unified audio recorder
  useEffect(() => {
    if (transcription && onTranscriptUpdate) {
      onTranscriptUpdate(transcription);
    }
  }, [transcription, onTranscriptUpdate]);

  useEffect(() => {
    if (visitNote && onSoapNoteUpdate) {
      // Extract the content string from visitNote object
      const noteContent = typeof visitNote === 'string' ? visitNote : visitNote.content || '';
      onSoapNoteUpdate(noteContent);
    }
  }, [visitNote, onSoapNoteUpdate]);

  // Manual retry function
  const handleManualRetry = useCallback(() => {
    setConnectionError(null);
    setRetryCount(0);
    const userName = providerInfo?.name || healthcareInfo?.patientName || 'Participant';
    joinSessionAsync(sessionToJoin, zoomTopic, zoomPasscode, userName, 'host');
  }, [sessionToJoin, zoomTopic, zoomPasscode, providerInfo, healthcareInfo, joinSessionAsync]);

  // Render error state
  if (zoomError || connectionError) {
    return (
      <div className="error-state">
        <div className="ankr-error-icon">⚠️</div>
        <h4>{zoomError?.title || "Connection Error"}</h4>
        <p className="small mb-3">{zoomError?.message || connectionError?.message}</p>
        {zoomError?.suggestion && (
          <p className="small"><strong>Suggestion:</strong> {zoomError.suggestion}</p>
        )}
        {connectionError?.canRetry && !connectionError?.isRetrying && (
          <button 
            className="button secondary mt-3" 
            onClick={handleManualRetry}
          >
            Retry connecting
          </button>
        )}
        {connectionError?.isRetrying && (
          <div className="mt-3 align-center" style={{textAlign:'center'}}>
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <p className="zoom-placeholder-text">This is taking a bit longer. Is your internet working?</p>
            <p class="zoom-placeholder-subtext">Attempting to reconnect to our global streaming network...</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`zoom-container ${viewport.isMobile ? 'mobile-zoom-container' : ''}`}>
      {/* Show placeholder when not connected */}
      {!isSessionActive && !isConnecting && !isAutoJoining && (
        <>
          <div className="zoom-placeholder-icon rounded pl-2 mb-2"></div>
          <div className="zoom-placeholder-text">I'm ready</div>
          <div className="zoom-placeholder-subtext">
            {zoomTopic ? `Last meeting: ${zoomTopic}` : 'Select a case to start the visit...'}
          </div>
          {/* Show audio visualizer if recording audio */}
          {isAudioRecording && (
            <div className="mt-3">
              <AudioVisualizer 
                audioLevel={audioLevel}
                isRecording={isAudioRecording}
                microphoneStatus={microphoneStatus}
                size="medium"
              />
              <div className="mt-2 text-sm text-muted">
                Recording audio for transcription
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Show auto-join or connecting state */}
      {(isConnecting || isAutoJoining) && !isSessionActive && (
        <>
          <div className="zoom-placeholder-icon emoji">⏳</div>
          <div className="zoom-placeholder-text">
            {isAutoJoining ? "Hey! I found a visit you are in the middle of... let's get you back in" : "Connecting to Meeting..."}
          </div>
          <div className="zoom-placeholder-subtext">
            {isAutoJoining && autoJoinMeetingName ? autoJoinMeetingName : "Please wait while Mira and I set up your video visit"}
          </div>
        </>
      )}
      
      {/* Zoom UI Container */}
      <div 
        ref={uiToolkitContainerRef} 
        className={`zoom-ui-container ${
          viewport.isMobile ? 'mobile-zoom-ui' : 
          viewport.isTablet ? 'tablet-zoom-ui' : 
          'desktop-zoom-ui'
        }`}
        style={{ 
          display: isSessionActive ? 'block' : 'none' 
        }}
      />

      {/* Always-on clickable hotspot aligned to Zoom's Leave button on mobile */}
      {viewport.isMobile && isSessionActive && (
        <button
          aria-label="Leave meeting"
          title="Leave"
          onClick={triggerLeave}
          style={{
            position: 'fixed',
            // safe-area aware top/right positioning
            top: `calc(env(safe-area-inset-top, 0px) + 8px)`,
            right: `calc(env(safe-area-inset-right, 0px) + 8px)`,
            width: 48,
            height: 48,
            // make it invisible but tappable
            background: 'transparent',
            border: 'none',
            padding: 0,
            margin: 0,
            zIndex: 10000000050,
            pointerEvents: 'auto',
            // ensure tap works on iOS Safari
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            // expand hit target slightly
            borderRadius: 24,
            cursor: 'pointer'
          }}
        />
      )}
    </div>
  );
}

export default ZoomVideoSDK;